import axios from 'axios';

const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;

/**
 * Extract coordinates from a string (lat,lng, Google Maps link, etc.)
 */
export function extractCoordsFromText(text) {
  if (!text) return null;
  let raw = String(text).trim();
  if (!raw) return null;
  raw = raw.replace(/,\+-/g, ',-').replace(/,\+/g, ',');

  // Pattern: @lat,lng
  const atMatch = raw.match(/@\s*(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (atMatch) {
    const lat = parseFloat(atMatch[1]);
    const lng = parseFloat(atMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  // Pattern: !3dLAT!4dLNG (Google Maps internal format)
  const bangMatch = raw.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (bangMatch) {
    const lat = parseFloat(bangMatch[1]);
    const lng = parseFloat(bangMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  // Pattern: Query params q=lat,lng or ll=lat,lng
  try {
    if (raw.startsWith('http')) {
      const u = new URL(raw);
      const candidates = [
        u.searchParams.get('q'),
        u.searchParams.get('ll'),
        u.searchParams.get('daddr'),
        u.searchParams.get('saddr'),
        u.searchParams.get('query')
      ].filter(Boolean);

      for (const cand of candidates) {
        const m = String(cand).match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/);
        if (m) {
          const lat = parseFloat(m[1]);
          const lng = parseFloat(m[2]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
        }
      }
    }
  } catch (e) {
    // ignore URL parse errors
  }

  // Pattern: Plain lat,lng or lat lng
  const plainMatch = raw.match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/);
  if (plainMatch) {
    const lat = parseFloat(plainMatch[1]);
    const lng = parseFloat(plainMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  return null;
}

/**
 * Resolve a map link using the backend normalization service.
 * This handles shortened links (maps.app.goo.gl, goo.gl/maps) safely.
 */
export async function resolveMapLink(link) {
  if (!link || typeof link !== 'string') return null;
  const trimmed = link.trim();
  if (!trimmed) return null;
  if (!/^https?:\/\//i.test(trimmed)) return null;

  try {
    // The backend endpoint /api/ubicacion/normalize-map-link expects { url: string }
    const res = await axios.post('/api/ubicacion/normalize-map-link', { url: trimmed });
    // The backend returns { url: string, coords: {lat, lng} | null }
    return {
      link: res.data?.url || trimmed,
      coords: res.data?.coords || null,
      place_name: res.data?.place_name || null
    };
  } catch (err) {
    console.error('[transportHelper] Error resolving map link:', err);
    return null;
  }
}

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenizeSearchText(value) {
  const stopWords = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'en', 'av', 'avenida', 'cc', 'centro']);
  return Array.from(
    new Set(
      normalizeSearchText(value)
        .split(' ')
        .map((token) => token.trim())
        .filter((token) => token && token.length >= 3 && !stopWords.has(token))
    )
  );
}

function getDistanceKm(a, b) {
  const lat1 = Number(a?.lat);
  const lng1 = Number(a?.lng);
  const lat2 = Number(b?.lat);
  const lng2 = Number(b?.lng);
  if (!Number.isFinite(lat1) || !Number.isFinite(lng1) || !Number.isFinite(lat2) || !Number.isFinite(lng2)) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinLng * sinLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return earthRadiusKm * c;
}

const KNOWN_CITY_ALIASES = {
  caracas: ['caracas', 'ccs'],
  maracaibo: ['maracaibo'],
  valencia: ['valencia'],
  barquisimeto: ['barquisimeto'],
  maracay: ['maracay'],
  maturin: ['maturin'],
  merida: ['merida'],
  puerto_ordaz: ['puerto ordaz', 'puerto ordaz ciudad guayana', 'ciudad guayana'],
  san_cristobal: ['san cristobal'],
  barcelona: ['barcelona'],
  lecheria: ['lecheria'],
};

function detectCityKeys(text) {
  const normalized = normalizeSearchText(text);
  const found = [];
  for (const [cityKey, aliases] of Object.entries(KNOWN_CITY_ALIASES)) {
    if (aliases.some((alias) => normalized.includes(normalizeSearchText(alias)))) {
      found.push(cityKey);
    }
  }
  return found;
}

function dedupeLocationSuggestions(results) {
  const seen = new Set();
  const output = [];
  for (const result of Array.isArray(results) ? results : []) {
    const lat = Number(result?.coords?.lat);
    const lng = Number(result?.coords?.lng);
    const label = normalizeSearchText(result?.label || result?.shortLabel || '');
    const key = Number.isFinite(lat) && Number.isFinite(lng)
      ? `${lat.toFixed(5)},${lng.toFixed(5)}|${label}`
      : label;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    output.push(result);
  }
  return output;
}

async function searchPhotonSuggestions(query, { proximity, limit = 6 } = {}) {
  try {
    const params = {
      q: String(query || '').trim(),
      limit: String(Math.max(1, Math.min(10, Number(limit) || 6))),
    };

    if (proximity && Number.isFinite(Number(proximity.lat)) && Number.isFinite(Number(proximity.lng))) {
      params.near_lat = String(Number(proximity.lat));
      params.near_lng = String(Number(proximity.lng));
    }

    const res = await axios.get('/api/ubicacion/geocode', { params });
    const rows = Array.isArray(res.data?.results) ? res.data.results : [];

    return rows
      .map((row) => {
        const lat = Number(row?.lat);
        const lng = Number(row?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

        const parts = [row?.street, row?.housenumber, row?.city, row?.state, row?.country]
          .map((value) => String(value || '').trim())
          .filter(Boolean);

        const shortLabel = String(row?.label || row?.name || row?.street || row?.city || '').trim();
        const label = parts.length > 0 ? parts.join(', ') : shortLabel;

        return {
          id: `photon:${String(row?.osm_type || 'loc')}:${String(row?.osm_id || `${lat},${lng}`)}`,
          label: label || shortLabel,
          shortLabel: shortLabel || label || 'Ubicación',
          coords: { lat, lng },
          address: String(row?.street || '').trim(),
          placeType: [row?.osm_type || 'place'].filter(Boolean),
          featureType: String(row?.osm_type || 'place').trim(),
          categoryKey: String(row?.osm_type || 'place').trim(),
          context: [row?.city, row?.state, row?.country].map((value) => String(value || '').trim()).filter(Boolean),
          source: 'photon',
        };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

async function searchMapTilerSuggestions(query, { proximity, limit = 6 } = {}) {
  if (!MAPTILER_KEY) return [];

  const params = new URLSearchParams({
    key: MAPTILER_KEY,
    limit: String(Math.max(1, Math.min(10, Number(limit) || 6))),
    language: 'es',
    country: 've',
    autocomplete: 'true',
  });

  if (proximity && Number.isFinite(Number(proximity.lng)) && Number.isFinite(Number(proximity.lat))) {
    params.set('proximity', `${Number(proximity.lng)},${Number(proximity.lat)}`);
  }

  const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(String(query || '').trim())}.json?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  const features = Array.isArray(data?.features) ? data.features : [];

  return features
    .map((feature) => {
      const center = Array.isArray(feature?.center) ? feature.center : null;
      const placeType = Array.isArray(feature?.place_type) ? feature.place_type : [];
      const featureType = typeof feature?.feature_type === 'string' ? feature.feature_type.trim() : '';
      const context = Array.isArray(feature?.context) ? feature.context : [];
      const lng = center?.[0];
      const lat = center?.[1];
      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null;
      return {
        id: String(feature?.id || `${lat},${lng}:${feature?.place_name || feature?.text || query}`),
        label: String(feature?.place_name || feature?.text || query).trim(),
        shortLabel: String(feature?.text || feature?.place_name || query).trim(),
        coords: { lat: Number(lat), lng: Number(lng) },
        address: String(feature?.address || '').trim(),
        placeType,
        featureType,
        categoryKey: String(placeType[0] || featureType || '').trim(),
        context: context
          .map((item) => String(item?.text || item?.place_name || '').trim())
          .filter(Boolean),
        source: 'maptiler',
      };
    })
    .filter(Boolean);
}

function rankLocationSuggestions(results, { query, proximity, localContextText } = {}) {
  const queryText = normalizeSearchText(query);
  const queryTokens = tokenizeSearchText(queryText);
  const contextTokens = tokenizeSearchText(localContextText).filter((token) => !queryTokens.includes(token));
  const queryCityKeys = detectCityKeys(queryText);
  const contextCityKeys = detectCityKeys(localContextText);
  const preferredCityKeys = queryCityKeys.length > 0 ? queryCityKeys : contextCityKeys;

  return [...(Array.isArray(results) ? results : [])]
    .map((result, index) => {
      const haystack = normalizeSearchText([
        result?.shortLabel,
        result?.label,
        result?.address,
        ...(Array.isArray(result?.context) ? result.context : []),
      ].join(' '));

      let score = 0;

      if (queryText) {
        if (haystack.startsWith(queryText)) score += 180;
        if (haystack.includes(queryText)) score += 90;
      }

      for (const token of queryTokens) {
        if (haystack.includes(token)) {
          score += 28;
        } else {
          score -= 18;
        }
      }

      for (const token of contextTokens) {
        if (haystack.includes(token)) {
          score += token.length >= 6 ? 40 : 24;
        }
      }

      const resultCityKeys = detectCityKeys(haystack);
      if (preferredCityKeys.length > 0) {
        const hasPreferredCity = preferredCityKeys.some((cityKey) => resultCityKeys.includes(cityKey));
        const hasDifferentKnownCity = resultCityKeys.some((cityKey) => !preferredCityKeys.includes(cityKey));
        if (hasPreferredCity) {
          score += 160;
        }
        if (!hasPreferredCity && hasDifferentKnownCity) {
          score -= 260;
        }
      }

      const distanceKm = proximity ? getDistanceKm(proximity, result?.coords) : null;
      if (distanceKm != null) {
        if (distanceKm <= 2) score += 170;
        else if (distanceKm <= 5) score += 130;
        else if (distanceKm <= 15) score += 90;
        else if (distanceKm <= 40) score += 55;
        else if (distanceKm <= 120) score += 20;
        else if (distanceKm > 350) score -= 120;
        else if (distanceKm > 700) score -= 220;
      }

      return {
        ...result,
        distanceKm,
        _rankScore: score,
        _originalIndex: index,
      };
    })
    .sort((a, b) => {
      if (b._rankScore !== a._rankScore) return b._rankScore - a._rankScore;
      const distanceA = Number.isFinite(a.distanceKm) ? a.distanceKm : Number.POSITIVE_INFINITY;
      const distanceB = Number.isFinite(b.distanceKm) ? b.distanceKm : Number.POSITIVE_INFINITY;
      if (distanceA !== distanceB) return distanceA - distanceB;
      return a._originalIndex - b._originalIndex;
    })
    .map(({ _rankScore, _originalIndex, ...result }) => result);
}

/**
 * Search for location suggestions using MapTiler.
 */
export async function searchLocationSuggestions(query, { proximity, limit = 5, localContextText = '' } = {}) {
  const trimmed = String(query || '').trim();
  if (!trimmed || trimmed.length < 3) return [];

  try {
    const desiredLimit = Math.max(1, Math.min(10, Number(limit) || 5));
    const [maptilerResults, photonResults] = await Promise.all([
      searchMapTilerSuggestions(trimmed, { proximity, limit: Math.max(desiredLimit, 6) }),
      searchPhotonSuggestions(trimmed, { proximity, limit: Math.max(desiredLimit, 8) }),
    ]);

    const merged = dedupeLocationSuggestions([
      ...photonResults,
      ...maptilerResults,
    ]);

    return rankLocationSuggestions(merged, {
      query: trimmed,
      proximity,
      localContextText,
    }).slice(0, desiredLimit);
  } catch {
    return [];
  }
}

function extractRouteGeometry(payload) {
  const visit = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
      try {
        return visit(JSON.parse(value));
      } catch {
        return null;
      }
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = visit(item);
        if (found) return found;
      }
      return null;
    }

    if (typeof value !== 'object') return null;

    if (value.type === 'LineString' && Array.isArray(value.coordinates)) {
      return value;
    }

    if (value.type === 'Feature' && value.geometry) {
      return visit(value.geometry);
    }

    if (value.type === 'FeatureCollection' && Array.isArray(value.features)) {
      return visit(value.features);
    }

    return visit(value.geometry) || visit(value.route) || visit(value.routes) || visit(value.features) || visit(value.path);
  };

  return visit(payload);
}

function toFiniteDistance(value) {
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? Number(n) : null;
}

function pickShortestRouteGeometry(routes) {
  if (!Array.isArray(routes) || routes.length === 0) return null;

  const bestRoute = routes
    .map((route) => {
      const geometry = extractRouteGeometry(route);
      const distance =
        toFiniteDistance(route?.distance_km) ??
        toFiniteDistance(route?.distanceKm) ??
        toFiniteDistance(route?.summary?.distance_km) ??
        toFiniteDistance(route?.summary?.distanceKm);

      return geometry ? { geometry, distance } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    })[0];

  return bestRoute?.geometry || null;
}


/**
 * Fetch route geometry between two points.
 */
export async function fetchRouteGeometry(from, to) {
  if (!from || !to) return null;
  if (from.lat == null || from.lng == null || to.lat == null || to.lng == null) return null;

  try {
    // We use the quote endpoint but just for the route visualization
    const res = await axios.post('/api/transport/quote', {
      from_location: {
        coords: {
          lat: Number(from.lat),
          lng: Number(from.lng),
        },
      },
      to_location: {
        coords: {
          lat: Number(to.lat),
          lng: Number(to.lng),
        },
      },
      kind: 'ride', // default behavior
      vehicle_type: 'moto'
    });

    const shortestGeometry =
      pickShortestRouteGeometry(res.data?.quote?.routes) ||
      pickShortestRouteGeometry(res.data?.routes);

    if (shortestGeometry) return shortestGeometry;

    const geometry =
      extractRouteGeometry(res.data?.quote?.route) ||
      extractRouteGeometry(res.data?.quote?.routes) ||
      extractRouteGeometry(res.data?.route) ||
      extractRouteGeometry(res.data?.routes) ||
      extractRouteGeometry(res.data);

    if (geometry) return geometry;
    return null;
  } catch (err) {
    console.error('[transportHelper] Error fetching route:', err);
    return null;
  }
}

/**
 * Extract a likely place name from a Google Maps URL if available.
 */
export function extractPlaceNameFromUrl(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return null;
  try {
    const u = new URL(urlStr.trim());
    const parts = String(u.pathname || '').split('/').filter(Boolean);

    // /maps/place/Some+Place/
    const placeIdx = parts.findIndex(p => p === 'place');
    if (placeIdx >= 0 && parts[placeIdx + 1]) {
      return decodeURIComponent(parts[placeIdx + 1].replace(/\+/g, ' ')).trim();
    }

    // /maps/search/Some+Place/
    const searchIdx = parts.findIndex(p => p === 'search');
    if (searchIdx >= 0 && parts[searchIdx + 1]) {
      const candidate = decodeURIComponent(parts[searchIdx + 1].replace(/\+/g, ' ')).trim();
      if (candidate && !candidate.match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/)) {
        return candidate;
      }
    }

    const q = u.searchParams.get('q') || u.searchParams.get('query');
    if (q) {
      const candidate = String(q).replace(/\+/g, ' ').trim();
      if (candidate && !candidate.match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/)) {
        return candidate;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
}
